import { z } from 'zod';
import { versionName } from './agent-changes';
// Tools and read models for calls, chats and email threads. The browser fetches them from the app's endpoints and
// the evals keep fixtures; both describe them to Via through the functions below, so Via sees the same shapes either
// way. The records below are the parts of the app's responses these functions read.
const DEFAULT_LIMIT = 20;
const TURNS_SHOWN = 120;
const MAX_TURN_CHARS = 500;
const MAX_ACTION_CHARS = 400;
const MAX_REASONING_CHARS = 300;
const conversationKindSchema = z.enum(['call', 'chat', 'email']);
const conversationSummarySchema = z.object({
    conversationId: z.string(),
    kind: conversationKindSchema,
    agentId: z.string().optional(),
    agentName: z.string().optional().describe('Name the agent by this, never by its id'),
    startedAt: z.string(),
    durationSeconds: z.number().optional(),
    endedReason: z
        .string()
        .optional()
        .describe("Calls: how the call ended, as the organization labels it. Chats: the chat's status."),
    customer: z.string().optional().describe('Phone number, customer id or email address'),
    summary: z.string().optional(),
    subject: z.string().optional().describe("An email thread's subject"),
    campaign: z.string().optional(),
    isTest: z.boolean().optional(),
    qaPassed: z.boolean().optional().describe('The overall QA result, when QA scored it'),
    failedCriteria: z.array(z.string()).optional().describe('QA criteria it failed'),
    sentiment: z.string().optional().describe('Its QA sentiment label'),
    hasFeedback: z.boolean().optional().describe('A reviewer left feedback on it'),
    actionsCalled: z
        .array(z.string())
        .optional()
        .describe('Actions the agent called, when the transcripts were read')
});
export const findConversationsToolSchema = {
    name: 'find_conversations',
    description: "Find the organization's calls, chats and email threads, newest first, with each one's summary, how it ended and its QA result. Narrow by kind, agent, date range, how it ended, QA result, a failed QA criterion, sentiment, reviewer feedback, campaign, customer, an action the agent called, or words in the summary. `matched` counts every match, so comparing two date ranges shows a trend. Read one with get_conversation.",
    inputSchema: z.object({
        kind: conversationKindSchema
            .optional()
            .describe('Only calls, chats or email threads; all three when left out'),
        agentId: z.string().optional().describe('From list_agents'),
        from: z.string().optional().describe('ISO date or date-time; defaults to 7 days ago'),
        to: z.string().optional().describe('ISO date or date-time; defaults to now'),
        endedReasons: z
            .array(z.string())
            .optional()
            .describe('Calls: ended reasons exactly as find_conversations reports them. Chats: statuses (active, handedOff, terminated, abandoned).'),
        qa: z.enum(['passed', 'failed']).optional().describe('Only conversations QA passed, or failed'),
        failedCriterion: z
            .string()
            .optional()
            .describe('Only conversations that failed this QA criterion, by name'),
        sentiment: z
            .string()
            .optional()
            .describe("A QA sentiment label, spelled as the organization's labels are"),
        hasFeedback: z.boolean().optional().describe('Only conversations a reviewer left feedback on'),
        campaign: z.string().optional().describe('Calls from this campaign'),
        customer: z.string().optional().describe('Part of a phone number, customer id or email address'),
        action: z
            .string()
            .optional()
            .describe('Only calls and chats where the agent called this action, by name. Slower: it reads transcripts.'),
        search: z
            .string()
            .optional()
            .describe('Text the summary (or an email\'s subject) must contain, such as "refund" or "wrong address"'),
        limit: z.number().int().min(1).max(50).optional().describe(`Defaults to ${DEFAULT_LIMIT}`)
    }),
    outputSchema: z.object({
        conversations: z.array(conversationSummarySchema),
        matched: z.number().describe('How many matched before the limit')
    })
};
const transcriptTurnSchema = z.object({
    turn: z.number().describe('Its place in the transcript, for referring back to it'),
    speaker: z
        .enum(['customer', 'agent', 'action', 'person', 'event'])
        .describe('agent is the AI agent; person is a human the conversation was handed to; action is a tool call'),
    text: z.string(),
    atSeconds: z.number().optional(),
    status: z.string().optional().describe('Emails: sent, drafted, failed or skipped'),
    skipReason: z.string().optional().describe("Emails: why the agent didn't send this reply"),
    feedback: z
        .object({ text: z.string(), resolved: z.boolean(), comments: z.number() })
        .optional()
        .describe('What a reviewer said about this turn')
});
const actionCallSchema = z.object({
    turn: z.number().optional().describe('Where in the transcript it was called'),
    name: z.string(),
    actionId: z
        .string()
        .optional()
        .describe('The action as it was then; get_action reads it only while the agent still has it'),
    arguments: z.string(),
    result: z.string().optional(),
    error: z.string().optional(),
    workflowRunId: z.string().optional().describe('The run of its workflow, step by step'),
    failedSteps: z.array(z.string()).optional().describe('Workflow steps that failed while it ran')
});
const workflowRunRefSchema = z.object({
    workflowRunId: z.string(),
    status: z.enum(['completed', 'failed', 'running']),
    stepsCompleted: z.array(z.string()),
    stepsFailed: z.array(z.string())
});
const agentVersionStandingSchema = z.object({
    name: z
        .string()
        .describe('The version it ran on, as the Versions tab names it, such as v18. One that was never published reads "Saved draft" or "Earlier saved draft"'),
    isLatestPublished: z.boolean().describe('Whether it is the latest published version'),
    newerPublished: z
        .object({ versionId: z.string(), name: z.string(), publishedAt: z.string() })
        .optional()
        .describe('The latest published version, when it was published after the version this ran on. Missing for the saved draft, which is newer than anything published'),
    newerDraft: z
        .object({ versionId: z.string() })
        .optional()
        .describe('A draft saved after the version it ran on and not published. Nothing in it has reached a caller, and none of it was in this conversation'),
    unsavedChangesInEditor: z
        .boolean()
        .optional()
        .describe('True when the open editor holds changes to this agent that are not saved. They are in no version, so none of them was in this conversation')
});
export const getConversationToolSchema = {
    name: 'get_conversation',
    description: "Read one call, chat or email thread: how it ended, the agent version it ran on and what has been published, saved or left unsaved in the editor since, the numbered transcript with reviewer feedback on the turns it was left on, the actions the agent called with their results, errors, failed steps and workflow runs, the pre-call and post-call workflow runs, what the analysis extracted, the variables it had, and QA with each criterion's reasoning. Emails also carry the agent's reasoning and why a reply was skipped. Transcripts can include personal details; quote only what the user needs.",
    inputSchema: z.object({
        conversationId: z.string(),
        kind: conversationKindSchema,
        fromTurn: z
            .number()
            .int()
            .min(0)
            .optional()
            .describe(`Read a long transcript from this turn on. Without it, one over ${TURNS_SHOWN} turns comes back as its start and its end.`)
    }),
    outputSchema: conversationSummarySchema.extend({
        agentVersionId: z
            .string()
            .optional()
            .describe('The agent version that handled it (for a chat, the one it started on). Read what the agent was then with get_agent_draft and this versionId; the agent may have changed since'),
        agentVersion: agentVersionStandingSchema
            .optional()
            .describe("Where that version stands now. A change published or saved after it, or still unsaved, was not in this conversation. Missing when the user can't read the agent's versions"),
        direction: z.enum(['inbound', 'outbound', 'web']).optional(),
        endedBy: z.string().optional(),
        transferredTo: z.string().optional(),
        retryOf: z
            .array(z.string())
            .optional()
            .describe('Earlier calls in its campaign retry chain, nearest first'),
        handedOffTo: z.string().optional().describe('Chats: the help desk a person took it over in'),
        csat: z.boolean().optional().describe('Chats: whether the visitor said they were satisfied'),
        transcript: z.array(transcriptTurnSchema),
        turnsLeftOut: z
            .object({ from: z.number(), to: z.number() })
            .optional()
            .describe('Turns of a long transcript that were left out; read them with fromTurn'),
        actions: z.array(actionCallSchema),
        workflows: z.object({
            preCall: workflowRunRefSchema.optional().describe('The run that prepared the conversation'),
            postCall: workflowRunRefSchema.optional().describe('The run after it ended')
        }),
        extracted: z.record(z.string(), z.unknown()).describe("What the agent's analysis extracted"),
        variables: z.object({
            passedIn: z.record(z.string(), z.unknown()).optional().describe('Values passed in when it started'),
            session: z
                .record(z.string(), z.unknown())
                .optional()
                .describe("Values the agent's Set Variable steps stored")
        }),
        qa: z.object({
            scored: z.boolean().describe('Whether QA scored it'),
            passed: z.boolean().optional(),
            sentiment: z.string().optional(),
            criteria: z.array(z.object({ name: z.string(), passed: z.boolean(), reasoning: z.string().optional() })),
            highlights: z.array(z.object({
                turn: z.number(),
                kind: z.enum(['success', 'failure']),
                note: z.string(),
                criterion: z.string().optional()
            }))
        }),
        reasoning: z
            .array(z.object({ kind: z.enum(['think', 'action', 'response']), text: z.string() }))
            .optional()
            .describe("Emails: the agent's reasoning steps while it worked on the thread")
    })
};
/** Where the version a conversation ran on stands now: whether it's still the latest published, and what came after. */
export const describeVersionStanding = ({ versionId, startedAt }, { published, saved, unsavedChanges }) => {
    const [latest] = published;
    const position = published.findIndex((version) => version.versionId === versionId);
    const isNewestSave = saved.versionId === versionId;
    // A version that was never published was the newest save when its conversation ran, so a published version is newer
    // only when it came after that moment. The newest save has nothing after it, whatever the clocks say.
    const publishedAfterItRan = !isNewestSave && Date.parse(startedAt) < (latest?.deployedAt ?? 0);
    const isBehindLatest = position === -1 ? publishedAfterItRan : position > 0;
    return {
        name: versionName(published, versionId) ?? (isNewestSave ? 'Saved draft' : 'Earlier saved draft'),
        isLatestPublished: position === 0,
        ...(latest &&
            isBehindLatest && {
            newerPublished: {
                versionId: latest.versionId,
                name: `v${published.length}`,
                publishedAt: new Date(latest.deployedAt).toISOString()
            }
        }),
        ...(saved.versionId &&
            !saved.deployedAt &&
            !isNewestSave && { newerDraft: { versionId: saved.versionId } }),
        ...(unsavedChanges && { unsavedChangesInEditor: true })
    };
};
/** A conversation with where its version stands now, when its agent's versions could be read. */
export const withVersionStanding = (detail, versions) => detail.agentVersionId && versions
    ? {
        ...detail,
        agentVersion: describeVersionStanding({ versionId: detail.agentVersionId, startedAt: detail.startedAt }, versions)
    }
    : detail;
const shorten = (text, max) => (text.length > max ? `${text.slice(0, max)}…` : text);
const secondsBetween = (start, end) => {
    if (!start || !end)
        return undefined;
    const seconds = Math.round((Date.parse(end) - Date.parse(start)) / 1000);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
};
const textOf = (value) => (typeof value === 'string' ? value : JSON.stringify(value ?? null));
// Keys starting with "_" are Strada's own bookkeeping, which the app hides too.
const visibleFields = (fields = {}) => Object.fromEntries(Object.entries(fields).filter(([key]) => !key.startsWith('_')));
const orUndefined = (fields) => Object.keys(fields).length > 0 ? fields : undefined;
const qaSummary = (qa = {}) => {
    const failedCriteria = Object.entries(qa.rubricOutcomes ?? {}).flatMap(([name, passed]) => passed ? [] : [name]);
    return {
        ...(typeof qa.qaOverallPassed === 'boolean' && { qaPassed: qa.qaOverallPassed }),
        ...(failedCriteria.length > 0 && { failedCriteria }),
        ...(qa.qaSentiment && { sentiment: qa.qaSentiment })
    };
};
const feedbackOf = (message) => {
    const feedback = 'feedback' in message ? message.feedback : undefined;
    return typeof feedback === 'object' &&
        feedback !== null &&
        'text' in feedback &&
        typeof feedback.text === 'string'
        ? feedback
        : undefined;
};
const callActionNames = (call) => (call.transcript ?? []).flatMap((message) => message.role === 'tool_calls' ? message.toolCalls.map(({ function: { name } }) => name) : []);
const chatActionNames = (chat) => (chat.transcript?.messages ?? []).flatMap((item) => 'role' in item && item.role === 'tool' ? [item.toolName] : []);
// What a list row can say about reviewer feedback and actions depends on whether it carries the transcript.
const transcriptFacts = (names, messages) => messages
    ? {
        hasFeedback: messages.some((message) => feedbackOf(message) !== undefined),
        actionsCalled: [...new Set(names)]
    }
    : {};
export const summarizeCall = (call, qa) => ({
    conversationId: call.callId,
    kind: 'call',
    agentId: call.agentId,
    startedAt: call.startedAt ?? call.createdAt,
    durationSeconds: secondsBetween(call.startedAt, call.endedAt),
    endedReason: call.endedReason,
    customer: call.customerNumber,
    summary: call.summary,
    ...(call.campaignName && { campaign: call.campaignName }),
    isTest: call.isTest,
    ...qaSummary(qa ?? call),
    ...transcriptFacts(call.transcript && callActionNames(call), call.transcript)
});
export const summarizeChat = (chat, qa) => ({
    conversationId: chat.chatId,
    kind: 'chat',
    agentId: chat.agentId,
    startedAt: chat.createdAt,
    ...(chat.duration !== undefined && { durationSeconds: Math.round(chat.duration) }),
    endedReason: chat.status,
    customer: chat.customerUniqueId,
    summary: chat.summary,
    isTest: chat.isTest,
    ...qaSummary(qa ?? chat),
    ...transcriptFacts(chat.transcript && chatActionNames(chat), chat.transcript?.messages)
});
export const summarizeEmailThread = (thread) => ({
    conversationId: thread.threadId,
    kind: 'email',
    ...(thread.vapiAssistantId && { agentId: thread.vapiAssistantId }),
    startedAt: new Date(thread.createdAt).toISOString(),
    customer: thread.customerEmail ?? thread.customerAddress,
    ...(thread.latestMessagePreview && { summary: shorten(thread.latestMessagePreview, MAX_TURN_CHARS) }),
    ...(thread.subject && { subject: thread.subject }),
    isTest: thread.isTest,
    ...(thread.hasFeedback !== undefined && { hasFeedback: thread.hasFeedback }),
    ...(thread.actionsUsed && { actionsCalled: thread.actionsUsed })
});
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_DAYS = 7;
/** The date range find_conversations covers, as ISO strings: the input's, or the last 7 days. */
export const conversationDateRange = ({ from, to }, now = Date.now()) => ({
    from: new Date(from ? Date.parse(from) : now - DEFAULT_DAYS * DAY_MS).toISOString(),
    to: new Date(to ? Date.parse(to) : now).toISOString()
});
const includesText = (value, text) => !text || (value?.toLowerCase().includes(text.trim().toLowerCase()) ?? false);
const matches = (conversation, input, from, to) => (!input.kind || conversation.kind === input.kind) &&
    (!input.agentId || conversation.agentId === input.agentId) &&
    conversation.startedAt >= from &&
    conversation.startedAt <= to &&
    (!input.endedReasons?.length ||
        (conversation.endedReason !== undefined && input.endedReasons.includes(conversation.endedReason))) &&
    (!input.qa || conversation.qaPassed === (input.qa === 'passed')) &&
    (!input.failedCriterion ||
        (conversation.failedCriteria ?? []).some((name) => includesText(name, input.failedCriterion))) &&
    (!input.sentiment || conversation.sentiment?.toLowerCase() === input.sentiment.toLowerCase()) &&
    (input.hasFeedback === undefined || (conversation.hasFeedback ?? false) === input.hasFeedback) &&
    includesText(conversation.campaign, input.campaign) &&
    includesText(conversation.customer, input.customer) &&
    (!input.action || (conversation.actionsCalled ?? []).some((name) => includesText(name, input.action))) &&
    (includesText(conversation.summary, input.search) || includesText(conversation.subject, input.search));
/** Filters, sorts and trims summaries for find_conversations. Filters the app's endpoints already applied are harmless to repeat. */
/** Adds each conversation's agent name, so Via names agents as the app does rather than repeating their ids. */
export const withAgentName = (agentNames) => (conversation) => {
    const agentName = conversation.agentId && agentNames.get(conversation.agentId);
    return agentName ? { ...conversation, agentName } : conversation;
};
export const findConversations = (conversations, input, now = Date.now()) => {
    const { from, to } = conversationDateRange(input, now);
    const matching = conversations
        .filter((conversation) => matches(conversation, input, from, to))
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    return { conversations: matching.slice(0, input.limit ?? DEFAULT_LIMIT), matched: matching.length };
};
// Keeps the start and the end of a long transcript, since how it opened and how it ended matter most, and says which
// turns were left out. With fromTurn it reads on from there instead.
const pageTurns = (turns, fromTurn) => {
    if (fromTurn !== undefined) {
        const rest = turns.filter(({ turn }) => turn >= fromTurn);
        const shown = rest.slice(0, TURNS_SHOWN);
        const after = rest[TURNS_SHOWN];
        const last = rest.at(-1);
        return {
            transcript: shown,
            ...(after && last && { turnsLeftOut: { from: after.turn, to: last.turn } })
        };
    }
    if (turns.length <= TURNS_SHOWN)
        return { transcript: turns };
    const half = TURNS_SHOWN / 2;
    const head = turns.slice(0, half);
    const tail = turns.slice(-half);
    const firstLeftOut = turns[half];
    const lastLeftOut = turns[turns.length - half - 1];
    return {
        transcript: [...head, ...tail],
        ...(firstLeftOut && lastLeftOut && { turnsLeftOut: { from: firstLeftOut.turn, to: lastLeftOut.turn } })
    };
};
const withFeedback = (turn, message) => {
    const feedback = feedbackOf(message);
    return feedback
        ? {
            ...turn,
            feedback: {
                text: shorten(feedback.text, MAX_TURN_CHARS),
                resolved: feedback.resolved ?? false,
                comments: feedback.comments?.length ?? 0
            }
        }
        : turn;
};
// System messages are left out: on a call they hold the agent's compiled prompt, which Via never shows.
const callTurn = (message, turn) => {
    const at = { turn, atSeconds: message.secondsFromStart };
    switch (message.role) {
        case 'user':
            return [{ ...at, speaker: 'customer', text: shorten(message.message, MAX_TURN_CHARS) }];
        case 'bot':
            return [
                withFeedback({ ...at, speaker: 'agent', text: shorten(message.message, MAX_TURN_CHARS) }, message)
            ];
        case 'tool_calls':
            return [
                {
                    ...at,
                    speaker: 'action',
                    text: shorten(message.toolCalls
                        .map(({ function: { name, arguments: args } }) => `Called ${name}(${args})`)
                        .join('; '), MAX_ACTION_CHARS)
                }
            ];
        case 'tool_call_result':
            return [
                {
                    ...at,
                    speaker: 'action',
                    text: shorten(`${message.name} returned ${message.result}`, MAX_ACTION_CHARS)
                }
            ];
        default:
            return [];
    }
};
const chatTurn = (item, turn) => {
    if ('eventType' in item)
        return [{ turn, speaker: 'event', text: item.eventType }];
    switch (item.role) {
        case 'user':
            return [{ turn, speaker: 'customer', text: shorten(item.content, MAX_TURN_CHARS) }];
        case 'bot':
            return [withFeedback({ turn, speaker: 'agent', text: shorten(item.content, MAX_TURN_CHARS) }, item)];
        case 'agent':
            return [withFeedback({ turn, speaker: 'person', text: shorten(item.content, MAX_TURN_CHARS) }, item)];
        case 'tool':
            return [
                {
                    turn,
                    speaker: 'action',
                    text: shorten(`${item.toolName}(${item.toolArguments ?? ''}) returned ${item.toolResult ?? 'nothing'}`, MAX_ACTION_CHARS)
                }
            ];
        default:
            return [];
    }
};
const describeActions = (records, nodeErrors = {}, turnOf) => records.map(({ toolCallId, toolId, toolName, toolArguments, result, error, workflowRunId }) => {
    const turn = toolCallId ? turnOf(toolCallId) : undefined;
    const failedSteps = toolCallId ? (nodeErrors[toolCallId] ?? []).map(({ nodeName }) => nodeName) : [];
    return {
        ...(turn !== undefined && { turn }),
        name: toolName,
        ...(toolId && { actionId: toolId }),
        arguments: shorten(JSON.stringify(toolArguments), MAX_ACTION_CHARS),
        ...(result !== null && { result: shorten(textOf(result), MAX_ACTION_CHARS) }),
        ...(error !== null && { error: shorten(textOf(error), MAX_ACTION_CHARS) }),
        ...(workflowRunId && { workflowRunId }),
        ...(failedSteps.length > 0 && { failedSteps })
    };
});
const describeRun = (state) => {
    if (!state)
        return undefined;
    const stepsFailed = (state.appsFailed ?? []).map(({ nodeName }) => nodeName);
    const status = stepsFailed.length > 0 ? 'failed' : state.completedAt ? 'completed' : 'running';
    return {
        workflowRunId: state.workflowRunId,
        status,
        stepsCompleted: (state.appsCompleted ?? []).map(({ nodeName }) => nodeName),
        stepsFailed
    };
};
const NOT_SCORED = { scored: false, criteria: [], highlights: [] };
const describeQa = (qa) => {
    if (!qa)
        return NOT_SCORED;
    const criterionNames = new Map(qa.criterionResults.map(({ criterionId, criterion }) => [criterionId, criterion.name]));
    return {
        scored: true,
        ...(qa.qaOverallPassed !== null && { passed: qa.qaOverallPassed }),
        ...(qa.overallSentimentLabel && { sentiment: qa.overallSentimentLabel.label }),
        criteria: qa.criterionResults.map(({ passed, feedback, criterion }) => ({
            name: criterion.name,
            passed,
            ...(feedback && { reasoning: shorten(feedback, MAX_TURN_CHARS) })
        })),
        highlights: qa.transcriptHighlights.map(({ messageIndex, highlightType, reason, criterionId }) => {
            const criterion = criterionId ? criterionNames.get(criterionId) : undefined;
            return {
                turn: messageIndex,
                kind: highlightType,
                note: shorten(reason, MAX_TURN_CHARS),
                ...(criterion && { criterion })
            };
        })
    };
};
const qaFields = (qa) => {
    const described = describeQa(qa);
    const failedCriteria = described.criteria.flatMap(({ name, passed }) => (passed ? [] : [name]));
    return {
        qa: described,
        ...(described.passed !== undefined && { qaPassed: described.passed }),
        ...(failedCriteria.length > 0 && { failedCriteria }),
        ...(described.sentiment && { sentiment: described.sentiment })
    };
};
const DIRECTIONS = {
    inboundPhoneCall: 'inbound',
    outboundPhoneCall: 'outbound',
    webCall: 'web'
};
export const describeCall = (call, records = {}, fromTurn) => {
    const messages = call.transcript ?? [];
    const turns = messages.flatMap((message, index) => callTurn(message, index));
    const turnOf = (toolCallId) => {
        const index = messages.findIndex((message) => message.role === 'tool_calls' && message.toolCalls.some(({ id }) => id === toolCallId));
        return index >= 0 ? index : undefined;
    };
    const direction = call.type ? DIRECTIONS[call.type] : undefined;
    const retryOf = call.campaignParentCallIds ?? [];
    return {
        // QA comes from the detailed result below, not the list-level fields.
        ...summarizeCall(call, {}),
        ...(call.agentVersionId && { agentVersionId: call.agentVersionId }),
        ...(direction && { direction }),
        endedBy: call.endedBy,
        ...(call.transferDestination && { transferredTo: call.transferDestination }),
        ...(retryOf.length > 0 && { retryOf }),
        ...pageTurns(turns, fromTurn),
        actions: describeActions(records.actions ?? [], records.nodeErrors, turnOf),
        workflows: { preCall: describeRun(records.preCall), postCall: describeRun(records.postCall) },
        extracted: visibleFields(call.analysis),
        variables: {
            passedIn: orUndefined(visibleFields(call.callVariables)),
            session: orUndefined(visibleFields(call.sessionVariables))
        },
        ...qaFields(records.qa)
    };
};
export const describeChat = (chat, records = {}, fromTurn) => {
    const messages = chat.transcript?.messages ?? [];
    const turns = messages.flatMap((item, index) => chatTurn(item, index));
    const turnOf = (toolCallId) => {
        const index = messages.findIndex((item) => 'role' in item && item.role === 'tool' && item.toolCallId === toolCallId);
        return index >= 0 ? index : undefined;
    };
    return {
        ...summarizeChat(chat, {}),
        ...(records.agentVersionId && { agentVersionId: records.agentVersionId }),
        ...(chat.handedOffTo && { handedOffTo: chat.handedOffTo }),
        ...(chat.csatIsSatisfied !== undefined && { csat: chat.csatIsSatisfied }),
        ...pageTurns(turns, fromTurn),
        actions: describeActions(records.actions ?? [], records.nodeErrors, turnOf),
        workflows: { preCall: describeRun(records.preCall), postCall: describeRun(records.postCall) },
        extracted: visibleFields(chat.analysis),
        variables: {
            passedIn: orUndefined(visibleFields(chat.chatVariables)),
            session: orUndefined(visibleFields(chat.sessionVariables))
        },
        ...qaFields(records.qa)
    };
};
const EMAIL_SPEAKERS = { user: 'customer', bot: 'agent', agent: 'person' };
const mergedFeedback = (feedback = []) => feedback.length === 0
    ? undefined
    : {
        text: shorten(feedback.map(({ text }) => text).join(' / '), MAX_TURN_CHARS),
        resolved: feedback.every(({ resolved }) => resolved === true),
        comments: feedback.reduce((total, { comments }) => total + (comments?.length ?? 0), 0)
    };
export const describeEmailThread = (thread, fromTurn) => {
    const turns = (thread.messages ?? []).map((message, turn) => {
        const feedback = mergedFeedback(message.feedback);
        return {
            turn,
            speaker: EMAIL_SPEAKERS[message.role],
            text: shorten(message.bodyText ?? message.subject, MAX_TURN_CHARS),
            ...(message.status && { status: message.status }),
            ...(message.skipReasoning && { skipReason: shorten(message.skipReasoning, MAX_TURN_CHARS) }),
            ...(feedback && { feedback })
        };
    });
    const steps = thread.executionTrace ?? [];
    const actions = steps.flatMap(({ actionRun }) => actionRun
        ? [
            {
                name: actionRun.actionName,
                ...(actionRun.actionId && { actionId: actionRun.actionId }),
                arguments: shorten(JSON.stringify(actionRun.arguments), MAX_ACTION_CHARS),
                ...(actionRun.result !== undefined && {
                    result: shorten(textOf(actionRun.result), MAX_ACTION_CHARS)
                }),
                ...(actionRun.error && { error: shorten(actionRun.error, MAX_ACTION_CHARS) }),
                ...(actionRun.workflowRunId && { workflowRunId: actionRun.workflowRunId })
            }
        ]
        : []);
    const reasoning = steps.flatMap(({ type, reasoning: thought, actionName, result }) => {
        const text = type === 'action' ? `Ran ${actionName ?? 'an action'}` : (thought ?? result);
        return text ? [{ kind: type, text: shorten(text, MAX_REASONING_CHARS) }] : [];
    });
    return {
        ...summarizeEmailThread(thread),
        hasFeedback: turns.some(({ feedback }) => feedback !== undefined),
        ...pageTurns(turns, fromTurn),
        actions,
        workflows: {},
        extracted: {},
        variables: {},
        qa: NOT_SCORED,
        reasoning
    };
};
//# sourceMappingURL=conversations.js.map